import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useTokens, RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { getAccessibleTextOnBackground } from '../utils/luminance';

// ============================================================================
// Component
// ============================================================================

/**
 * Site Map page intro: centered heading and blurb that opens the page below the
 * hero — mirrors the InteractiveMapIntro layout but omits the CTA since the
 * site map is purely informational (the map image sits directly below it).
 */
export function SiteMapIntro() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  // Prefer primary on the page background; fall back when contrast is too low.
  const tokens = useTokens();
  const headingColor = useMemo(
    () => getAccessibleTextOnBackground(tokens?.color?.background, tokens?.color?.primary),
    [tokens],
  );

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-background)] pt-[40px] pb-16 md:pb-24"
    >
      <div className="mx-auto px-3 sm:px-4 lg:px-6 text-center">
        <RichTextContent
          path="sitemapintro.heading"
          label="Heading"
          defaultValue="<p>Explore Our Community Property Map</p>"
          as="h2"
          className="text-balance text-[clamp(2rem,8.8vw,3.5rem)] sm:text-[clamp(1rem,4.4vw,2.5rem)] uppercase tracking-wide leading-[1.1] mb-6"
          style={{ color: headingColor, fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}
        />

        <div className="max-w-3xl mx-auto">
          <RichTextContent
            path="sitemapintro.body"
            label="Body Text"
            as="div"
            className="font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_a]:text-primary [&_a]:underline"
          />
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'SiteMapIntro',
  displayName: 'Site Map Intro',
  tokens: {
    'sitemapintro.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Explore Our Community Property Map</p>',
    },
    'sitemapintro.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultValue:
        '<p>Take a bird\'s-eye look at <span data-type="field-token" data-field="property_name"></span>. Our community site map lays out every building, amenity, and pathway so you can picture yourself at home before you ever step onsite.</p>',
    },
  },
  slotGroups: {},
};

(SiteMapIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default SiteMapIntro;
